import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard,
  LineChart,
  Trophy,
  BellRing,
  LogOut,
  Compass,
  Beaker,
  Sparkles,
  BookText,
  Building2,
  Copy as CopyIcon,
  Bot,
  Activity,
  Users,
  FileText,
  Layers,
  Map,
} from "lucide-react";
import { Toaster } from "sonner";
import NotificationBell from "@/components/NotificationBell";
import EnvBadge from "@/components/EnvBadge";
import PWAInstallButton from "@/components/PWAInstallButton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/markets", label: "Markets", icon: Compass, id: "nav-markets" },
  { to: "/trade", label: "Trade", icon: LineChart, id: "nav-trade" },
  { to: "/options", label: "Options", icon: Layers, id: "nav-options" },
  { to: "/bots", label: "AutoPilot", icon: Bot, id: "nav-bots" },
  { to: "/ai-council", label: "Council", icon: Users, id: "nav-ai-council" },
  { to: "/backtest", label: "Backtest", icon: Beaker, id: "nav-backtest" },
  { to: "/strategy-ai", label: "Coach", icon: Sparkles, id: "nav-strategy-ai" },
  { to: "/report", label: "Report", icon: FileText, id: "nav-report" },
  { to: "/journal", label: "Journal", icon: BookText, id: "nav-journal" },
  { to: "/broker", label: "Broker", icon: Building2, id: "nav-broker" },
  { to: "/copy", label: "Copy", icon: CopyIcon, id: "nav-copy" },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy, id: "nav-leaderboard" },
  { to: "/alerts", label: "Alerts", icon: BellRing, id: "nav-alerts" },
  { to: "/health", label: "Health", icon: Activity, id: "nav-health" },
  { to: "/roadmap", label: "Roadmap", icon: Map, id: "nav-roadmap" },
];

const ADMIN_NAV = [
  { to: "/ops", label: "Ops", icon: Activity, id: "nav-ops" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const onLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Toaster position="top-right" richColors closeButton />
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center gap-8">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="brand-link">
            <img
              src="/icons/icon-192.png"
              alt="TradePilot"
              className="w-9 h-9 rounded-lg shadow-sm ring-1 ring-brand/10"
              data-testid="brand-logo"
            />
            <span className="font-display font-semibold text-lg tracking-tight text-brand-ink">
              Trade<span className="text-brand">Pilot</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-0.5 ml-4 whitespace-nowrap">
            {[...NAV, ...(user?.role === "admin" ? ADMIN_NAV : [])].map(({ to, label, icon: Icon, id }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={id}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-2.5 py-2 rounded-full text-[13px] font-medium transition-colors whitespace-nowrap ${
                    isActive
                      ? "bg-brand-tint text-brand-deep"
                      : "text-gray-600 hover:text-brand-deep hover:bg-brand-tint/60"
                  }`
                }
              >
                <Icon size={14} />
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <PWAInstallButton />
            <EnvBadge />
            <NavLink
              to="/pro"
              data-testid="nav-pro-cta"
              className="hidden sm:inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.1em] bg-brand-gradient text-white rounded-full px-3 py-1.5 shadow-sm hover:opacity-90 transition-opacity"
            >
              Pro
            </NavLink>
            <NotificationBell />
            <NavLink
              to={`/profile/${user?.id}`}
              data-testid="nav-profile"
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <Avatar className="h-8 w-8 border border-gray-200">
                <AvatarImage src={user?.avatar_url} alt={user?.name} />
                <AvatarFallback className="text-xs bg-brand-tint text-brand-deep">{(user?.name || "?").slice(0, 2).toUpperCase()}</AvatarFallback>
              </Avatar>
              <span className="hidden sm:inline text-sm font-medium">{user?.name}</span>
            </NavLink>
            <button
              onClick={onLogout}
              data-testid="logout-btn"
              className="p-2 rounded-full hover:bg-gray-100 text-gray-600 hover:text-brand-deep transition-colors"
              title="Sign out"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        <nav className="md:hidden flex items-center gap-1 px-6 pb-3 overflow-x-auto">
          {[...NAV, ...(user?.role === "admin" ? ADMIN_NAV : [])].map(({ to, label, icon: Icon, id }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`${id}-mobile`}
              className={({ isActive }) =>
                `shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  isActive ? "bg-brand text-white" : "text-gray-600 bg-gray-50"
                }`
              }
            >
              <Icon size={14} />
              {label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-6 py-10 animate-fade-in">
        <Outlet />
      </main>

      <footer className="border-t border-gray-200 py-6 mt-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-2">
          <p className="text-xs text-gray-500">
            TradePilot · Paper trading · Not financial advice
          </p>
          <p className="text-xs text-gray-400 font-mono">Powered by CoinGecko · Alpha Vantage · Claude</p>
        </div>
      </footer>
    </div>
  );
}
